import { useState, useMemo } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus, Search, LogIn, LogOut, Eye, Edit, ArrowLeft } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Zone {
  id: number
  name: string
  eventName: string
  eventId: number
}

interface CheckInStat {
  date: string
  count: number
}

interface Props extends Omit<AppShellProps, 'children'> {
  zones: Zone[]
  event: { id: number; name: string } | null
  checkInStats: CheckInStat[]
  urls: AppShellProps['urls'] & {
    newZone: string
    zones: string
    dashboard: string
    [key: string]: string
  }
  translations: AppShellProps['translations'] & {
    zones: string
    newZone: string
    backToDashboard: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Index(props: Props) {
  const { zones, event, checkInStats, urls, translations: t, ...shell } = props
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search.trim()) return zones
    const q = search.toLowerCase()
    return zones.filter(
      (z) =>
        z.name?.toLowerCase().includes(q) ||
        z.eventName?.toLowerCase().includes(q),
    )
  }, [zones, search])

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={t.zones} />

      <div className="mx-auto max-w-6xl space-y-6 py-8 px-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => router.visit(urls.dashboard)}
              className="text-[#91918E] hover:bg-[#F1F1EF]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-[#37352F]">{t.zones}</h1>
              {event && (
                <p className="mt-1 text-sm text-[#91918E]">Filtered by: {event.name}</p>
              )}
            </div>
          </div>
          <Button
            className="bg-[#2383E2] hover:bg-[#1b6ec2]"
            onClick={() => router.visit(urls.newZone)}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t.newZone}
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#91918E]" />
          <Input
            placeholder="Search zones..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 border-[#E8E8E5]"
          />
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg border border-[#E8E8E5]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F1F1EF]">
                <th className="px-4 py-3 text-left font-medium text-[#91918E]">Event</th>
                <th className="px-4 py-3 text-left font-medium text-[#91918E]">Name</th>
                <th className="px-4 py-3 text-center font-medium text-[#91918E]">Check IN</th>
                <th className="px-4 py-3 text-center font-medium text-[#91918E]">Check OUT</th>
                <th className="px-4 py-3 text-center font-medium text-[#91918E]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-[#91918E]">
                    No zones found.
                  </td>
                </tr>
              ) : (
                filtered.map((zone) => (
                  <tr
                    key={zone.id}
                    className="border-t border-[#E8E8E5] transition-colors hover:bg-[#F1F1EF]"
                  >
                    <td className="px-4 py-3">
                      <span className="inline-block rounded bg-[#E8E8E5] px-2 py-0.5 text-xs font-medium text-[#37352F]">
                        {zone.eventName}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-[#37352F]">{zone.name}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <button
                          onClick={() => router.visit(urls[`newCheckIn_${zone.id}`])}
                          className="inline-flex items-center gap-1 rounded bg-[#2383E2] px-2 py-1 text-xs font-medium text-white"
                        >
                          <LogIn className="h-3 w-3" /> New Check IN
                        </button>
                        <button
                          onClick={() => router.visit(urls[`checkIns_${zone.id}`])}
                          className="inline-flex items-center gap-1 rounded border border-[#E8E8E5] px-2 py-1 text-xs text-[#37352F]"
                        >
                          View List
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <button
                          onClick={() => router.visit(urls[`newCheckOut_${zone.id}`])}
                          className="inline-flex items-center gap-1 rounded bg-amber-600 px-2 py-1 text-xs font-medium text-white"
                        >
                          <LogOut className="h-3 w-3" /> New Check OUT
                        </button>
                        <button
                          onClick={() => router.visit(urls[`checkOuts_${zone.id}`])}
                          className="inline-flex items-center gap-1 rounded border border-[#E8E8E5] px-2 py-1 text-xs text-[#37352F]"
                        >
                          View List
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => router.visit(urls[`showZone_${zone.id}`])}
                          className="rounded p-1.5 text-[#91918E] hover:bg-[#F1F1EF]"
                          title="Show"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => router.visit(urls[`editZone_${zone.id}`])}
                          className="rounded p-1.5 text-[#91918E] hover:bg-[#F1F1EF]"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Check-in Statistics */}
        {checkInStats.length > 0 && (
          <div>
            <h2 className="mb-4 text-lg font-semibold text-[#37352F]">Check-in Statistics</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {checkInStats.map((stat) => (
                <div key={stat.date} className="rounded-lg border border-[#2383E2] p-4 text-center">
                  <div className="text-sm font-medium text-[#91918E]">{stat.date}</div>
                  <div className="mt-1 text-3xl font-bold text-[#2383E2]">{stat.count}</div>
                  <div className="text-xs text-[#91918E]">Check-ins</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
